// web-common/src/progress.js — live render progress (D1-DESIGN §4). READ-ONLY over the B2
// RTDB fan-out, with the job ROW as the only truth:
//
//   /progress/<job_id>/steps/<seq>  — append-only step events (zero-padded keys = time order)
//   /progress/<job_id>/state        — the state mirror
//
// TWO RULES (B1 §4.3, restated for the client):
//   1. RTDB IS NEVER READ TO MAKE A DECISION — the watcher ALSO polls GET /v1/jobs/:id and
//      the row's state ALWAYS wins; terminal on the row ends the watch regardless of RTDB.
//   2. No RTDB configured (the local rig) -> the same watcher degrades to faster row polling;
//      steps narrate coarsely from row state. Screens cannot tell the difference.
//
// watchJob({ api, jobId, rtdb?, onStep?, onState?, pollMs? }) -> stop()

const TERMINAL = ['succeeded', 'failed', 'refused', 'cancelled'];

export function watchJob({ api, jobId, rtdb = null, onStep = null, onState = null, pollMs = null }) {
  let stopped = false;
  let unsubs = [];
  let lastRowState = null;

  // --- the truth: row polling (always on; slower when RTDB carries the live feed) ---
  const interval = pollMs || (rtdb ? 5000 : 2000);
  const poll = async () => {
    if (stopped) return;
    const r = await api.get(`jobs/${jobId}`);
    if (stopped) return;
    if (r.kind === 'ok') {
      if (r.body.state !== lastRowState) {
        lastRowState = r.body.state;
        if (onState) onState({ state: r.body.state, source: 'row', job: r.body });
      }
      if (TERMINAL.includes(r.body.state)) { stop(); return; } // the row ends the watch, always
    }
    timer = setTimeout(poll, interval);
  };
  let timer = setTimeout(poll, 250);

  // --- the live feed: RTDB listen (best-effort projection; never decides anything) ---
  if (rtdb) {
    (async () => {
      try {
        const { getDatabase, ref, onChildAdded, onValue } = await import('firebase/database');
        const db = typeof rtdb === 'object' && rtdb.app ? rtdb : getDatabase();
        const stepsRef = ref(db, `progress/${jobId}/steps`);
        const stateRef = ref(db, `progress/${jobId}/state`);
        const u1 = onChildAdded(stepsRef, (snap) => { if (!stopped && onStep) onStep(snap.val()); });
        const u2 = onValue(stateRef, (snap) => {
          const v = snap.val();
          if (!stopped && v && onState) onState({ ...v, source: 'rtdb' });
        });
        unsubs.push(u1, u2);
      } catch (e) {
        // RTDB unavailable -> the row polling carries it alone (by design, not an error)
        console.warn('progress: RTDB listen unavailable, polling only —', e && e.message);
      }
    })();
  }

  function stop() {
    if (stopped) return;
    stopped = true;
    clearTimeout(timer);
    for (const u of unsubs) { try { u(); } catch { /* already detached */ } }
    unsubs = [];
  }
  return stop;
}
